import React, { useContext, useMemo, useState } from 'react';
import styled from 'styled-components';

import getComparator from './helpers/comparatorHelper';
import { getOptionGroup, initialFields, SelectOption } from './helpers/memoriesHelper';
import { ThreadModuleContext } from 'src/presentation/components/pages/sidekick/threads/components/SidePanel/ThreadModuleContext';
import { getExternalActionInputProperties } from 'src/presentation/components/pages/sidekick/threads/components/SidePanel/ExternalActions/helpers';
import { TypeSpec } from 'src/models/Sidekick/SidekickModuleSpec';

export const ENTER_MANUALLY_OPTION = 'Enter Manually';

export const useCondition = ({
  fields,
  allowEnterManually,
  typeSpec,
  compatibilityMatch,
  includeOnlyNestedProps,
}: {
  fields: string[];
  allowEnterManually: boolean;
  typeSpec?: TypeSpec;
  compatibilityMatch: boolean;
  includeOnlyNestedProps: boolean;
}) => {
  const [currentFields, setCurrentFields] = useState(fields);

  const { definedMemories, extActionMems, typeSpecs } = useContext(ThreadModuleContext);

  const mems = useMemo(() => {
    // Only include memories that have a matching type for the input (typeSpec is exists)
    // somewhere within its properties.
    const defMemories = typeSpec
      ? getExternalActionInputProperties(definedMemories, typeSpec, typeSpecs, compatibilityMatch)
      : definedMemories;

    const mems: string[] = Object.keys(defMemories).map(key => key);

    mems.sort((a: string, b: string): number => a.localeCompare(b));
    const fieldOptions: SelectOption[] = mems.map((m: string) => {
      const opt = {
        label: m,
        value: m,
      };
      return opt;
    });

    if (allowEnterManually) {
      if (fieldOptions.length > 0) {
        fieldOptions.push({ type: 'SPLITTER' });
      }
      fieldOptions.push({
        label: <StyledEnterManuallyLabel>{ENTER_MANUALLY_OPTION}</StyledEnterManuallyLabel>,
        text: ENTER_MANUALLY_OPTION,
        value: ENTER_MANUALLY_OPTION,
      });
    }

    return fieldOptions;
  }, [definedMemories, allowEnterManually, typeSpec, typeSpecs, compatibilityMatch]);

  const [optGroups, lastSpec] = useMemo(() => {
    try {
      const { fields: initFields, lastSpec } = initialFields(
        currentFields,
        extActionMems,
        typeSpecs,
        definedMemories || {},
        includeOnlyNestedProps
      );

      const optGroups = getOptionGroup(
        initFields,
        extActionMems,
        typeSpecs,
        definedMemories || {},
        includeOnlyNestedProps,
        false
      );
      optGroups.unshift(mems);

      return [optGroups, lastSpec];
    } catch (e) {
      return [[], undefined];
    }
  }, [currentFields, typeSpecs, definedMemories, mems, extActionMems, includeOnlyNestedProps]);

  const comparatorOptions = useMemo(() => {
    if (!lastSpec) {
      return [];
    }
    const isExtActionMem = extActionMems.includes(currentFields[0]);

    return getComparator(lastSpec, isExtActionMem);
  }, [lastSpec, extActionMems, currentFields]);

  return { optGroups, currentFields, setCurrentFields, comparatorOptions, lastSpec };
};

const StyledEnterManuallyLabel = styled.span`
  color: ${p => p.theme.colors.green400};
`;
